import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { _ } from '/@/locale';
import { validateEmailV2 } from '/@/utils';

/**
 * Multi-step share popup for a seed-quality analysis, modelled on the shared
 * ShareItemPopup but local to bag-quality (which shares via redux PDF actions
 * rather than the id-based `useGeneratePDF` hook).
 *
 * Step 0 offers WhatsApp + Email. WhatsApp is public; the Email icon is only
 * shown to logged-in users (`canShare`) since sending by email needs a session.
 * Downloading stays a separate action outside this modal.
 */
const BagQualityShareModal = ({
    open,
    canShare,
    sending,
    whatsappLoading,
    onClose,
    onSendEmail,
    onWhatsApp,
}: {
    open: boolean;
    canShare: boolean;
    sending: boolean;
    whatsappLoading: boolean;
    onClose: () => void;
    onSendEmail: (email: string) => void;
    onWhatsApp: () => void;
}) => {
    const [step, setStep] = useState(0);
    const [email, setEmail] = useState('');

    // Reset to the initial step with an empty input each time the modal opens.
    useEffect(() => {
        if (open) {
            setStep(0);
            setEmail('');
        }
    }, [open]);

    const isValid = validateEmailV2(email.trim());

    const handleSend = () => {
        if (isValid) {
            onSendEmail(email.trim());
        }
    };

    const isEmailStep = step === 1;

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            maxWidth='sm'
            title={isEmailStep ? _('Ingresá un mail') : _('Compartí el resultado')}
            actionsProps={{ justifyContent: 'flex-end' }}
            actions={
                isEmailStep ? (
                    <>
                        <Button variant='text' color='secondary' onClick={() => setStep(0)} disabled={sending}>
                            {_('Cancelar')}
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={handleSend}
                            disabled={!isValid || sending}
                        >
                            {_('Enviar')}
                        </Button>
                    </>
                ) : undefined
            }
        >
            {isEmailStep ? (
                <Box>
                    <Typography variant='body1' color='primary' textAlign='center' sx={{ mb: 3 }}>
                        {_('El resultado del análisis se enviará por correo electrónico al destinatario que indiques.')}
                    </Typography>
                    <TextField
                        fullWidth
                        type='email'
                        value={email}
                        placeholder={_('Email')}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSend();
                            }
                        }}
                    />
                </Box>
            ) : (
                <Stack spacing={3} direction='column' alignItems='center' justifyContent='center'>
                    <Typography variant='body1' color='primary' textAlign='center'>
                        {_('Enviá el análisis de calidad de semilla por mail o por WhatsApp')}
                    </Typography>
                    <Stack direction='row' spacing={2}>
                        <Stack direction='column' spacing={1} alignItems='center'>
                            <IconButton onClick={onWhatsApp} color='primary' disabled={whatsappLoading}>
                                <WhatsAppIcon sx={{ fontSize: 24 }} />
                            </IconButton>
                            <Typography variant='caption' color='primary'>
                                {_('WhatsApp')}
                            </Typography>
                        </Stack>
                        {canShare && (
                            <Stack direction='column' spacing={1} alignItems='center'>
                                <IconButton onClick={() => setStep(1)} color='primary'>
                                    <EmailOutlinedIcon sx={{ fontSize: 24 }} />
                                </IconButton>
                                <Typography variant='caption' color='primary'>
                                    {_('Email')}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            )}
        </ModalV2>
    );
};

export default BagQualityShareModal;
